/**
 * SplitLayout Component
 *
 * Resizable split view for multiple agent sessions (up to 4 panels).
 * Ported from V1 with adaptations for V2's agent model.
 *
 * @module components/session/SplitLayout
 */

import { memo, useState, useRef, useCallback, useMemo } from 'react';
import { X, Maximize2, GripHorizontal, GripVertical } from 'lucide-react';
import { useAgentChat } from '../../contexts/AgentChatContext.jsx';
import { useUI } from '../../contexts/UIContext.jsx';

// ============================================
// Agent Branding Utils
// ============================================

/**
 * Generate a consistent HSL color from agent name
 * @param {string} name - Agent name to hash
 * @returns {string} HSL color string
 */
function generateAccentColor(name) {
  if (!name) return 'hsl(220, 70%, 50%)'; // Default indigo
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

// ============================================
// Resizable Divider
// ============================================

const Divider = memo(function Divider({ direction, panelRef, onResizeEnd }) {
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef(0);
  const startSize = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();

    const panel = panelRef.current;
    if (!panel) return;

    setIsDragging(true);
    startPos.current = direction === 'horizontal' ? e.clientX : e.clientY;
    startSize.current = direction === 'horizontal' ? panel.offsetWidth : panel.offsetHeight;
    panel.style.willChange = 'width, height';

    const handleMouseMove = (e) => {
      const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
      const delta = currentPos - startPos.current;
      const newSize = startSize.current + delta;
      const parent = panel.parentElement;
      const parentSize = direction === 'horizontal' ? parent.offsetWidth : parent.offsetHeight;
      const minSize = parentSize * 0.2;
      const maxSize = parentSize * 0.8;
      const clampedSize = Math.max(minSize, Math.min(maxSize, newSize));

      if (direction === 'horizontal') {
        panel.style.width = `${clampedSize}px`;
      } else {
        panel.style.height = `${clampedSize}px`;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      panel.style.willChange = 'auto';
      const parent = panel.parentElement;
      const parentSize = direction === 'horizontal' ? parent.offsetWidth : parent.offsetHeight;
      const currentSize = direction === 'horizontal' ? panel.offsetWidth : panel.offsetHeight;
      const percentage = (currentSize / parentSize) * 100;

      if (direction === 'horizontal') {
        panel.style.width = '';
      } else {
        panel.style.height = '';
      }

      onResizeEnd(percentage);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [direction, panelRef, onResizeEnd]);

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`
        flex items-center justify-center bg-gray-800 hover:bg-gray-700 transition-colors flex-shrink-0
        ${direction === 'horizontal' ? 'w-2 cursor-col-resize' : 'h-2 cursor-row-resize'}
        ${isDragging ? 'bg-indigo-500' : ''}
      `}
    >
      {direction === 'horizontal' ? (
        <GripVertical className="w-3 h-3 text-gray-600" />
      ) : (
        <GripHorizontal className="w-3 h-3 text-gray-600" />
      )}
    </div>
  );
});

// ============================================
// Panel Wrapper
// ============================================

const panelWrapperPropsAreEqual = (prev, next) => {
  return (
    prev.agentId === next.agentId &&
    prev.isFocused === next.isFocused &&
    prev.displayName === next.displayName &&
    prev.isActive === next.isActive &&
    prev.isProcessing === next.isProcessing &&
    prev.avatarUrl === next.avatarUrl &&
    prev.children === next.children
  );
};

const PanelWrapper = memo(function PanelWrapper({
  agentId,
  children,
  onRemove,
  onMaximize,
  onFocus,
  isFocused,
  displayName,
  isActive,
  isProcessing,
  avatarUrl,
}) {
  // Generate accent color from agent name
  const accentColor = useMemo(() => generateAccentColor(displayName), [displayName]);

  return (
    <div
      className={`
        flex flex-col h-full overflow-hidden rounded-lg border
        ${isFocused ? 'border-indigo-500/50' : 'border-gray-700'}
      `}
      onClick={() => onFocus(agentId)}
    >
      {/* Agent accent stripe */}
      <div
        className="h-1 flex-shrink-0"
        style={{ backgroundColor: accentColor }}
      />

      {/* Mini header */}
      <div className="flex items-center gap-2 px-2 py-1 bg-gray-800/80 border-b border-gray-700 flex-shrink-0">
        <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
        <span className="flex-1 text-xs font-medium text-gray-300 truncate">
          {displayName}
        </span>
        {isProcessing && (
          <span className="text-[10px] text-indigo-400 animate-pulse">Processing...</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onMaximize(agentId); }}
          className="p-0.5 rounded hover:bg-gray-600 text-gray-500 hover:text-gray-300"
          title="Maximize"
        >
          <Maximize2 className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(agentId); }}
          className="p-0.5 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400"
          title="Remove from split"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Content with watermark */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {children}
        {/* Watermark avatar - direct img for full resolution */}
        {avatarUrl && (
          <div className="absolute top-4 bottom-20 right-0 left-0 pointer-events-none flex items-start justify-end pr-8 overflow-hidden">
            <img
              src={avatarUrl}
              alt=""
              className="h-[70%] w-auto max-h-[400px] opacity-[0.07] object-contain"
              draggable={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}, panelWrapperPropsAreEqual);

// ============================================
// Main SplitLayout Component
// ============================================

export function SplitLayout({ splitSessions, renderPanel, agents = {} }) {
  const { agentSessions } = useAgentChat();
  const { removeFromSplit, clearSplit, setFocusedAgentId, focusedAgentId } = useUI();
  const [sizes, setSizes] = useState({ h: 50, v: 50 });

  const leftPanelRef = useRef(null);
  const topPanelRef = useRef(null);

  const handleRemove = useCallback((agentId) => {
    removeFromSplit(agentId);
  }, [removeFromSplit]);

  const handleMaximize = useCallback((agentId) => {
    clearSplit();
    setFocusedAgentId(agentId);
  }, [clearSplit, setFocusedAgentId]);

  const handleFocus = useCallback((agentId) => {
    setFocusedAgentId(agentId);
  }, [setFocusedAgentId]);

  const handleHorizontalResizeEnd = useCallback((percentage) => {
    setSizes(prev => ({ ...prev, h: percentage }));
  }, []);

  const handleVerticalResizeEnd = useCallback((percentage) => {
    setSizes(prev => ({ ...prev, v: percentage }));
  }, []);

  // Helper to get panel props for an agent
  const getPanelProps = useCallback((agentId) => {
    const session = agentSessions[agentId];
    const agent = agents[agentId];
    if (!session && !agent) return null;
    return {
      agentId,
      onRemove: handleRemove,
      onMaximize: handleMaximize,
      onFocus: handleFocus,
      isFocused: focusedAgentId === agentId,
      displayName: agent?.displayName || agent?.name || agentId,
      isActive: session?.active || false,
      isProcessing: session?.isProcessing || false,
      avatarUrl: agent?.avatarUrl || null,
    };
  }, [agentSessions, agents, focusedAgentId, handleRemove, handleMaximize, handleFocus]);

  const count = splitSessions.length;

  if (count === 0) return null;

  // Single panel
  if (count === 1) {
    const props = getPanelProps(splitSessions[0]);
    if (!props) return null;
    return (
      <div className="h-full p-1">
        <PanelWrapper {...props}>
          {renderPanel(splitSessions[0])}
        </PanelWrapper>
      </div>
    );
  }

  // Two panels - horizontal split
  if (count === 2) {
    const props0 = getPanelProps(splitSessions[0]);
    const props1 = getPanelProps(splitSessions[1]);
    if (!props0 || !props1) return null;
    return (
      <div className="h-full flex p-1 gap-0">
        <div ref={leftPanelRef} style={{ width: `${sizes.h}%` }} className="min-w-0 h-full">
          <PanelWrapper {...props0}>{renderPanel(splitSessions[0])}</PanelWrapper>
        </div>
        <Divider direction="horizontal" panelRef={leftPanelRef} onResizeEnd={handleHorizontalResizeEnd} />
        <div style={{ flex: 1 }} className="min-w-0 h-full">
          <PanelWrapper {...props1}>{renderPanel(splitSessions[1])}</PanelWrapper>
        </div>
      </div>
    );
  }

  // Three panels - all horizontal
  if (count === 3) {
    const props0 = getPanelProps(splitSessions[0]);
    const props1 = getPanelProps(splitSessions[1]);
    const props2 = getPanelProps(splitSessions[2]);
    if (!props0 || !props1 || !props2) return null;
    return (
      <div className="h-full flex p-1 gap-0">
        <div ref={leftPanelRef} style={{ width: '33.33%' }} className="min-w-0 h-full">
          <PanelWrapper {...props0}>{renderPanel(splitSessions[0])}</PanelWrapper>
        </div>
        <Divider direction="horizontal" panelRef={leftPanelRef} onResizeEnd={handleHorizontalResizeEnd} />
        <div ref={topPanelRef} style={{ width: '33.33%' }} className="min-w-0 h-full">
          <PanelWrapper {...props1}>{renderPanel(splitSessions[1])}</PanelWrapper>
        </div>
        <Divider direction="horizontal" panelRef={topPanelRef} onResizeEnd={handleVerticalResizeEnd} />
        <div style={{ flex: 1 }} className="min-w-0 h-full">
          <PanelWrapper {...props2}>{renderPanel(splitSessions[2])}</PanelWrapper>
        </div>
      </div>
    );
  }

  // Four panels - 2x2 grid
  if (count >= 4) {
    const props0 = getPanelProps(splitSessions[0]);
    const props1 = getPanelProps(splitSessions[1]);
    const props2 = getPanelProps(splitSessions[2]);
    const props3 = getPanelProps(splitSessions[3]);
    if (!props0 || !props1 || !props2 || !props3) return null;
    return (
      <div className="h-full flex flex-col p-1 gap-0">
        {/* Top row */}
        <div ref={topPanelRef} style={{ height: `${sizes.v}%` }} className="flex min-h-0">
          <div ref={leftPanelRef} style={{ width: `${sizes.h}%` }} className="min-w-0 h-full">
            <PanelWrapper {...props0}>{renderPanel(splitSessions[0])}</PanelWrapper>
          </div>
          <Divider direction="horizontal" panelRef={leftPanelRef} onResizeEnd={handleHorizontalResizeEnd} />
          <div style={{ flex: 1 }} className="min-w-0 h-full">
            <PanelWrapper {...props1}>{renderPanel(splitSessions[1])}</PanelWrapper>
          </div>
        </div>
        <Divider direction="vertical" panelRef={topPanelRef} onResizeEnd={handleVerticalResizeEnd} />
        {/* Bottom row */}
        <div style={{ flex: 1 }} className="flex min-h-0">
          <div style={{ width: `${sizes.h}%` }} className="min-w-0 h-full">
            <PanelWrapper {...props2}>{renderPanel(splitSessions[2])}</PanelWrapper>
          </div>
          <Divider direction="horizontal" panelRef={leftPanelRef} onResizeEnd={handleHorizontalResizeEnd} />
          <div style={{ flex: 1 }} className="min-w-0 h-full">
            <PanelWrapper {...props3}>{renderPanel(splitSessions[3])}</PanelWrapper>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default SplitLayout;
